import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { 
  sessionRegistrationsService,
  studentSessionsService,
  sessionsService, 
  coursesService, 
  homeworksService,
  homeworkSubmissionsService,
  attendanceService
} from '../../services/firestoreService';
import { storageService } from '../../services/storageService';
import { mergeSessionFeatureToggles } from '../../config/sessionFeatureToggles';
import './StudentSessions.css';
import SessionCard from '../shared/SessionCard';

const StudentSessions = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [submissionsMap, setSubmissionsMap] = useState({});
  const [attendanceMap, setAttendanceMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedHomework, setSelectedHomework] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'pending', 'active'

  useEffect(() => {
    if (!user || user.type !== 'student') return;

    // Subscribe to both registrations and directly assigned sessions
    let unsubscribeRegistrations = null;
    let unsubscribeAssignedSessions = null;

    const processSessions = async (registrations, assignedSessions) => {
      console.log('🔄 Processing sessions...', { 
        registrationsCount: registrations?.length || 0, 
        assignedSessionsCount: assignedSessions?.length || 0 
      });
      
      // Combine both sources
      const allSessions = [];

      // Process registrations
      const filteredRegs = (registrations || []).filter(reg => 
        reg.status === 'pending' || reg.status === 'active'
      );
      
      console.log('📝 Filtered registrations:', filteredRegs.length);

      for (const registration of filteredRegs) {
        console.log(`🔍 Processing registration for sessionId: ${registration.sessionId}, status: ${registration.status}`);
        const session = await sessionsService.getSession(registration.sessionId);
        if (!session) {
          console.warn(`⚠️ Session not found for registration: ${registration.sessionId}`);
          continue;
        }
        console.log(`✅ Session loaded: ${session.id}, title: ${session.title}`);

        // Get course if session has one
        let course = null;
        if (session.courseId) {
          course = await coursesService.getCourse(session.courseId);
        }

        // Get homeworks for this session (only if active and enabled)
        let homeworks = [];
        if (registration.status === 'active') {
          const allHomeworks = await homeworksService.getHomeworksBySession(session.id);
          console.log(`📚 Session ${session.id} - All homeworks:`, allHomeworks);
          console.log(`📚 Session ${session.id} - Homeworks count:`, allHomeworks.length);
          homeworks = allHomeworks.filter(hw => {
            const isEnabled = hw.enabled !== false;
            console.log(`📝 Homework "${hw.title}" - enabled: ${hw.enabled}, isEnabled: ${isEnabled}`);
            return isEnabled;
          });
          console.log(`✅ Session ${session.id} - Filtered enabled homeworks:`, homeworks.length);
          
          // Get submissions for each homework
          const submissionsPromises = homeworks.map(async (hw) => {
            const submission = await homeworkSubmissionsService.getStudentSubmission(hw.id, user.id);
            return { homeworkId: hw.id, submission };
          });
          const submissions = await Promise.all(submissionsPromises);
          const submissionsObj = {};
          submissions.forEach(({ homeworkId, submission }) => {
            if (submission) submissionsObj[homeworkId] = submission;
          });

          // Get attendance for this session
          const attendance = await attendanceService.getStudentAttendance(session.id, user.id);
          const attendanceObj = {};
          attendance.forEach(att => {
            const key = `${att.topicName}_${att.topicDate}`;
            attendanceObj[key] = att;
          });

          // Get topic classroom status for this session
          const topicClassroomStatus = await sessionRegistrationsService.getAllTopicClassroomStatus(session.id);

          allSessions.push({
            ...session,
            registration,
            course,
            homeworks,
            submissions: submissionsObj,
            attendance: attendanceObj,
            topicClassrooms: topicClassroomStatus
          });
        } else {
          // Get topic classroom status even for pending sessions
          const topicClassroomStatus = await sessionRegistrationsService.getAllTopicClassroomStatus(session.id);

          allSessions.push({
            ...session,
            registration,
            course,
            homeworks: [],
            submissions: {},
            attendance: {},
            topicClassrooms: topicClassroomStatus
          });
        }
      }

      // Process directly assigned sessions (not in registrations)
      const assignedSessionsList = assignedSessions || [];
      console.log('📋 Processing assigned sessions:', assignedSessionsList.length);
      
      for (const assignedSession of assignedSessionsList) {
        // Check if this session is already in registrations
        const existingReg = filteredRegs.find(reg => reg.sessionId === assignedSession.sessionId);
        if (existingReg) {
          console.log('⏭️ Skipping assigned session (already in registrations):', assignedSession.sessionId);
          continue; // Already processed above
        }

        console.log('✅ Processing assigned session:', assignedSession.sessionId, assignedSession);
        let session;
        try {
          session = await sessionsService.getSession(assignedSession.sessionId);
          if (!session) {
            // Session doesn't exist - clean up orphaned assignment silently
            try {
              await studentSessionsService.removeStudentSession(assignedSession.id, user.id);
              console.log('🧹 Cleaned up orphaned assignment:', assignedSession.sessionId);
            } catch (cleanupError) {
              // Silently handle cleanup errors - assignment may have already been removed
              console.debug('Cleanup note: Assignment may have already been removed');
            }
            continue;
          }
        } catch (error) {
          // If it's a "not found" error, try to clean up orphaned assignment
          if (error.code === 'not-found' || error.message?.includes('does not exist')) {
            try {
              await studentSessionsService.removeStudentSession(assignedSession.id, user.id);
              console.log('🧹 Cleaned up orphaned assignment:', assignedSession.sessionId);
            } catch (cleanupError) {
              // Silently handle cleanup errors
              console.debug('Cleanup note: Assignment may have already been removed');
            }
          } else {
            console.error('❌ Error fetching session:', assignedSession.sessionId, error);
          }
          continue;
        }

        // Get course if session has one
        let course = null;
        if (session.courseId) {
          course = await coursesService.getCourse(session.courseId);
        }

        // Create a mock registration object for directly assigned sessions
        const mockRegistration = {
          id: assignedSession.id,
          sessionId: assignedSession.sessionId,
          studentId: user.id,
          status: assignedSession.status === 'assigned' ? 'active' : assignedSession.status || 'active',
          assignedAt: assignedSession.assignedAt,
          assignedBy: assignedSession.assignedBy
        };

        // Get homeworks for this session (only if active)
        let homeworks = [];
        if (mockRegistration.status === 'active') {
          const allHomeworks = await homeworksService.getHomeworksBySession(session.id);
          console.log(`📚 Session ${session.id} - All homeworks loaded:`, allHomeworks);
          homeworks = allHomeworks.filter(hw => {
            // enabled should be true or undefined (defaults to true)
            // Only filter out if explicitly set to false
            const isEnabled = hw.enabled === true || hw.enabled === undefined || hw.enabled === null;
            if (!isEnabled) {
              console.log(`❌ Homework "${hw.title}" (ID: ${hw.id}) is disabled (enabled: ${hw.enabled})`);
            }
            return isEnabled;
          });
          console.log(`✅ Session ${session.id} - Enabled homeworks: ${homeworks.length} out of ${allHomeworks.length}`);
          
          // Get submissions for each homework
          const submissionsPromises = homeworks.map(async (hw) => {
            const submission = await homeworkSubmissionsService.getStudentSubmission(hw.id, user.id);
            return { homeworkId: hw.id, submission };
          });
          const submissions = await Promise.all(submissionsPromises);
          const submissionsObj = {};
          submissions.forEach(({ homeworkId, submission }) => {
            if (submission) submissionsObj[homeworkId] = submission;
          });

          // Get attendance for this session
          const attendance = await attendanceService.getStudentAttendance(session.id, user.id);
          const attendanceObj = {};
          attendance.forEach(att => {
            const key = `${att.topicName}_${att.topicDate}`;
            attendanceObj[key] = att;
          });

          // Get topic classroom status for this session
          const topicClassroomStatus = await sessionRegistrationsService.getAllTopicClassroomStatus(session.id);

          allSessions.push({
            ...session,
            registration: mockRegistration,
            course,
            homeworks,
            submissions: submissionsObj,
            attendance: attendanceObj,
            topicClassrooms: topicClassroomStatus
          });
        } else {
          // Get topic classroom status even for non-active assigned sessions
          const topicClassroomStatus = await sessionRegistrationsService.getAllTopicClassroomStatus(session.id);

          allSessions.push({
            ...session,
            registration: mockRegistration,
            course,
            homeworks: [],
            submissions: {},
            attendance: {},
            topicClassrooms: topicClassroomStatus
          });
        }
      }
      
      // Apply status filter
      let filteredSessions = allSessions;
      if (statusFilter === 'pending') {
        filteredSessions = allSessions.filter(s => s.registration?.status === 'pending');
      } else if (statusFilter === 'active') {
        filteredSessions = allSessions.filter(s => s.registration?.status === 'active');
      }
      
      setSessions(filteredSessions);

      // Build homeworks map (not used currently, but kept for potential future use)
      // const hwMap = {};
      // allSessions.forEach(session => {
      //   session.homeworks?.forEach(hw => {
      //     hwMap[hw.id] = hw;
      //   });
      // });

      // Build submissions map
      const subMap = {};
      allSessions.forEach(session => {
        Object.keys(session.submissions || {}).forEach(hwId => {
          subMap[hwId] = session.submissions[hwId];
        });
      });
      setSubmissionsMap(subMap);

      // Build attendance map
      const attMap = {};
      allSessions.forEach(session => {
        Object.keys(session.attendance || {}).forEach(key => {
          attMap[key] = session.attendance[key];
        });
      });
      setAttendanceMap(attMap);

      console.log('✅ Final sessions count:', filteredSessions.length);
      setLoading(false);
    };

    // Load initial data
    const loadInitialData = async () => {
      try {
        const [registrations, assignedSessions] = await Promise.all([
          sessionRegistrationsService.getStudentRegistrations(user.id),
          studentSessionsService.getStudentSessions(user.id)
        ]);
        console.log('📥 Initial load:', { 
          registrations: registrations.length, 
          assignedSessions: assignedSessions.length 
        });
        await processSessions(registrations, assignedSessions);
      } catch (error) {
        console.error('Error loading initial data:', error);
        setLoading(false);
      }
    };

    loadInitialData();

    // Subscribe to registrations
    unsubscribeRegistrations = sessionRegistrationsService.subscribeToStudentRegistrations(
      user.id,
      async (registrations) => {
        console.log('📥 Registration update:', registrations.length);
        // Get assigned sessions
        const assignedSessions = await studentSessionsService.getStudentSessions(user.id);
        await processSessions(registrations, assignedSessions);
      },
      (error) => {
        console.error('Error loading registrations:', error);
        setLoading(false);
      }
    );

    // Subscribe to directly assigned sessions
    unsubscribeAssignedSessions = studentSessionsService.subscribeToStudentSessions(
      user.id,
      async (assignedSessions) => {
        console.log('📥 Assigned sessions update:', assignedSessions.length);
        // Get current registrations
        const registrations = await sessionRegistrationsService.getStudentRegistrations(user.id);
        await processSessions(registrations, assignedSessions);
      },
      (error) => {
        console.error('Error loading assigned sessions:', error);
      }
    );

    return () => {
      if (unsubscribeRegistrations) unsubscribeRegistrations();
      if (unsubscribeAssignedSessions) unsubscribeAssignedSessions();
    };
  }, [user, statusFilter]);

  const handleSubmitHomework = async (homeworkId, answer, files) => {
    try {
      // Upload files if any
      let uploadedFiles = [];
      if (files && files.length > 0) {
        uploadedFiles = await storageService.uploadMultipleFiles(homeworkId, user.id, files);
      }

      // Create submission
      await homeworkSubmissionsService.createSubmission(homeworkId, {
        studentId: user.id,
        answer: answer.trim(),
        files: uploadedFiles
      });

      setSelectedHomework(null);
      alert('Homework submitted successfully!');
    } catch (error) {
      console.error('Error submitting homework:', error);
      alert('Error submitting homework. Please try again.');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return { label: '⏳ Pending Approval', type: 'pending' };
      case 'active':
        return { label: '✅ Active', type: 'active' };
      case 'completed':
        return { label: '✔️ Completed', type: 'completed' };
      default:
        return status ? { label: status, type: 'default' } : null;
    }
  };

  const normalizeDateValue = (value) => {
    if (!value) return null;
    if (typeof value === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split('-').map(Number);
        return new Date(year, month - 1, day);
      }
      const parsed = new Date(value);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    if (value.toDate) return value.toDate();
    if (value.seconds) return new Date(value.seconds * 1000);
    const date = value instanceof Date ? value : new Date(value);
    return isNaN(date.getTime()) ? null : date;
  };

  const formatDateDisplay = (value, options) => {
    const date = normalizeDateValue(value);
    return date ? date.toLocaleDateString(undefined, options) : 'TBD';
  };

  const formatDateTimeDisplay = (value) => {
    const date = normalizeDateValue(value);
    return date ? date.toLocaleString() : 'TBD';
  };

  const buildTopicDateKey = (topicName, dateValue) => {
    if (!topicName || !dateValue) return `${topicName}_unknown`;
    if (typeof dateValue === 'string') return `${topicName}_${dateValue}`;
    if (dateValue.toDate) return `${topicName}_${dateValue.toDate().toISOString()}`;
    if (dateValue instanceof Date) return `${topicName}_${dateValue.toISOString()}`;
    return `${topicName}_${String(dateValue)}`;
  };

  const buildSessionMeta = (session) => {
    const meta = [];
    if (session.startDate) {
      meta.push({ icon: '📅', label: 'Start', value: formatDateDisplay(session.startDate) });
    }
    if (session.endDate) {
      meta.push({ icon: '🏁', label: 'End', value: formatDateDisplay(session.endDate) });
    }
    if (session.totalDuration) {
      meta.push({ icon: '⏱️', label: 'Duration', value: `${session.totalDuration} hrs` });
    }
    if (session.registration?.assignedBy) {
      meta.push({ icon: '🧑‍🏫', label: 'Assigned By', value: session.registration.assignedBy });
    }
    if (session.registration?.assignedAt) {
      meta.push({ icon: '🕒', label: 'Assigned', value: formatDateDisplay(session.registration.assignedAt) });
    }
    return meta;
  };

  const buildTopicEntries = (session) => {
    if (!session.course?.topics?.length) return [];

    return session.course.topics.map((topic, idx) => {
      const topicDates = session.topicDates?.[topic.name] || [];
      const attendance = getAttendanceStatus(topic.name, topicDates);
      const topicClassroomEnabled = session.topicClassrooms?.[topic.name]?.enabled || false;

      const badges = [];
      if (attendance && session.registration?.status === 'active') {
        const badgeType = attendance.attended === attendance.total ? 'success' : 'warning';
        badges.push({ label: `📊 ${attendance.attended}/${attendance.total} attended`, type: badgeType });
      }
      if (topicClassroomEnabled && session.registration?.status === 'active') {
        badges.push({ label: 'Live Now', type: 'success' });
      }

      const datePills = topicDates.map((dateVal) => {
        const key = buildTopicDateKey(topic.name, dateVal);
        const attendanceRecord = attendanceMap[key];
        const jsDate = normalizeDateValue(dateVal);
        const label = jsDate ? jsDate.toLocaleDateString() : formatDateDisplay(dateVal);
        let type = 'default';
        if (attendanceRecord?.status === 'attended') {
          type = 'success';
        } else if (attendanceRecord?.status === 'not-attended' || attendanceRecord?.status === 'absent') {
          type = 'danger';
        }
        return {
          label: attendanceRecord?.status === 'attended' ? `${label} ✅` : label,
          type
        };
      });

      const classroomAccessEnabled = session.registration?.status === 'active' && session.registration?.classroomEnabled;
      const showInteractiveButton = classroomAccessEnabled && topicClassroomEnabled;
      const showVideoButton = classroomAccessEnabled && !!session.classroomUrl;

      let topicFooter = null;
      if (showInteractiveButton || showVideoButton) {
        topicFooter = (
          <div className="topic-classroom-actions">
            {showInteractiveButton && (
              <button
                onClick={() => navigate(`/student/classroom/${session.id}?tab=interactive&topic=${encodeURIComponent(topic.name)}`)}
                className="topic-classroom-btn"
              >
                🚀 Join Interactive Classroom
              </button>
            )}
            {showVideoButton && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(session.classroomUrl, '_blank');
                }}
                className="video-call-btn"
              >
                🎥 Join Video Call
              </button>
            )}
          </div>
        );
      }

      return {
        id: `${session.id}-${idx}`,
        name: topic.name,
        hours: topic.hours ? `${topic.hours}h` : undefined,
        description: topic.description,
        badges,
        dates: datePills,
        footer: topicFooter
      };
    });
  };

  const getAttendanceStatus = (topicName, topicDates) => {
    if (!topicDates || topicDates.length === 0) return null;
    
    const attendanceRecords = topicDates.map(date => {
      const key = `${topicName}_${date}`;
      return attendanceMap[key];
    }).filter(Boolean);

    if (attendanceRecords.length === 0) return null;

    const attended = attendanceRecords.filter(att => att.status === 'attended').length;
    const total = attendanceRecords.length;

    return { attended, total };
  };

  const getSessionFeatureToggles = (session) => mergeSessionFeatureToggles(session?.featureToggles);

  const hasInteractiveTopicsEnabled = (session) => {
    const topicStatus = session?.topicClassrooms || {};
    return Object.values(topicStatus).some(topic => topic?.enabled);
  };

  const getFirstInteractiveTopicName = (session) => {
    const topics = session?.course?.topics || [];
    const topicStatus = session?.topicClassrooms || {};
    const enabledTopic = topics.find(topic => topicStatus[topic.name]?.enabled);
    return enabledTopic?.name || topics[0]?.name || '';
  };

  const handleExperienceAction = (session, key) => {
    if (key === 'video') {
      // Always navigate to classroom with video tab (service will handle configuration check)
      navigate(`/student/classroom/${session.id}?tab=video`);
      return;
    }

    const tab = key === 'livecode' ? 'livecode' : 'interactive';
    const topicName = tab === 'interactive' ? getFirstInteractiveTopicName(session) : '';
    const topicParam = topicName ? `&topic=${encodeURIComponent(topicName)}` : '';
    navigate(`/student/classroom/${session.id}?tab=${tab}${topicParam}`);
  };

  const renderSessionExperiences = (session) => {
    const toggles = getSessionFeatureToggles(session);
    const registration = session.registration || {};
    const canAccessClassroom = registration.status === 'active' && registration.classroomEnabled;
    const lockedMessage = registration.status === 'pending'
      ? 'Pending approval'
      : registration.status === 'active' && !registration.classroomEnabled
        ? 'Waiting for coach'
        : 'Classroom locked';
    const experiences = [];

    if (toggles.showVideoTab) {
      // Check if Daily.co is configured (native video) or if external URL exists
      const useNativeVideo = process.env.REACT_APP_DAILY_API_KEY;
      const ready = canAccessClassroom && (useNativeVideo || Boolean(session.classroomUrl));
      experiences.push({
        key: 'video',
        label: 'Video Classroom',
        icon: '🎥',
        ready,
        status: ready ? 'Join live call' : (canAccessClassroom ? 'Link coming soon' : lockedMessage)
      });
    }

    if (toggles.showLiveCodeTab) {
      const ready = canAccessClassroom;
      experiences.push({
        key: 'livecode',
        label: 'Live Code Lab',
        icon: '💻',
        ready,
        status: ready ? 'Open workspace' : lockedMessage
      });
    }

    if (toggles.showInteractiveTab) {
      const hasTopics = hasInteractiveTopicsEnabled(session);
      const ready = canAccessClassroom && hasTopics;
      experiences.push({
        key: 'interactive',
        label: 'Interactive Arcade',
        icon: '🎮',
        ready,
        status: ready ? 'Jump into activities' : (!canAccessClassroom ? lockedMessage : 'Topics hidden')
      });
    }

    if (experiences.length === 0) {
      return null;
    }

    return (
      <div className="session-experience-panel">
        <div className="experience-panel-header">
          <h4>🚀 Classroom Experiences</h4>
          <p>Launch the modes your coach enabled for this session.</p>
        </div>
        <div className="experience-grid">
          {experiences.map(exp => (
            <button
              key={exp.key}
              type="button"
              className={`experience-chip ${exp.ready ? 'ready' : 'pending'}`}
              onClick={() => exp.ready && handleExperienceAction(session, exp.key)}
              disabled={!exp.ready}
            >
              <span className="experience-icon" aria-hidden="true">{exp.icon}</span>
              <div className="experience-details">
                <span className="experience-label">{exp.label}</span>
                <span className="experience-status">{exp.status}</span>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderSessionCard = (session) => {
    const statusBadge = getStatusBadge(session.registration?.status) || { label: 'Session', type: 'default' };
    const secondaryStatus = session.registration?.status === 'active'
      ? {
          label: session.registration?.classroomEnabled ? 'Classroom Enabled' : 'Classroom Pending',
          type: session.registration?.classroomEnabled ? 'active' : 'pending'
        }
      : null;
    const courseSection = session.course
      ? {
          title: `📚 ${session.course.title}`,
          description: session.course.description,
          objectives: session.courseObjects,
          expectations: session.expectations
        }
      : undefined;

    return (
      <SessionCard
        key={session.id}
        title={session.title}
        status={statusBadge}
        secondaryStatus={secondaryStatus}
        meta={buildSessionMeta(session)}
        course={courseSection}
        topics={buildTopicEntries(session)}
        defaultCollapsed={true}
      >
        {renderSessionExperiences(session)}
        {session.registration?.status === 'active' && (
          <div className="session-classroom-section">
            <div className="classroom-status-row">
              <span className="classroom-status-label">
                🎓 Classroom Access:
              </span>
              <span className={`classroom-status ${session.registration?.classroomEnabled ? 'enabled' : 'disabled'}`}>
                {session.registration?.classroomEnabled ? '✅ Enabled' : '❌ Disabled'}
              </span>
            </div>
            {session.registration?.classroomEnabled && (
              <p className="classroom-note">
                Use the topic cards below to jump into the live video call or interactive classroom for each module.
              </p>
            )}
          </div>
        )}

        {(() => {
          const hasActiveRegistration = session.registration?.status === 'active';
          const hasHomeworks = session.homeworks && session.homeworks.length > 0;
          console.log(`🔍 Session ${session.id} - Homework visibility check:`, {
            registrationStatus: session.registration?.status,
            hasActiveRegistration,
            homeworksCount: session.homeworks?.length || 0,
            hasHomeworks,
            homeworks: session.homeworks
          });
          return null;
        })()}
        {session.registration?.status === 'active' && session.homeworks && session.homeworks.length > 0 && (
          <div className="homeworks-section">
            <h3>📝 Homework Assignments</h3>
            {session.homeworks.map((homework) => {
              const submission = submissionsMap[homework.id];
              return (
                <div key={homework.id} className="homework-item">
                  <div className="homework-header">
                    <h4>{homework.title}</h4>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span className="due-date">
                        Due: {formatDateTimeDisplay(homework.dueDate)}
                      </span>
                      {homework.rewardPoints > 0 && (
                        <span style={{ 
                          padding: '4px 10px', 
                          backgroundColor: '#fef3c7', 
                          color: '#92400e', 
                          borderRadius: '12px', 
                          fontSize: '0.85rem',
                          fontWeight: '600'
                        }}>
                          ⭐ {homework.rewardPoints} Points
                        </span>
                      )}
                    </div>
                  </div>
                  {homework.description && (
                    <p className="homework-desc">{homework.description}</p>
                  )}
                  {homework.topicName && (
                    <p style={{ margin: '8px 0', color: '#64748b', fontSize: '0.9rem' }}>
                      <strong>Topic:</strong> {homework.topicName}
                    </p>
                  )}
                  {submission ? (
                    <div className="submission-status">
                      <span className="submitted-badge">✅ Submitted</span>
                      <p className="submission-date">
                        Submitted on: {formatDateTimeDisplay(submission.submittedAt)}
                      </p>
                    </div>
                  ) : (
                    <button
                      onClick={() => setSelectedHomework(homework)}
                      className="submit-btn"
                    >
                      📤 Submit Homework
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </SessionCard>
    );
  };

  if (loading) {
    return (
      <div className="student-sessions">
        <div className="empty-state">
          <p>Loading your sessions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="student-sessions">
      <div className="page-header">
        <h1>📚 Sessions Registered</h1>
        <div className="filter-buttons">
          <button
            className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            All
          </button>
          <button
            className={`filter-btn ${statusFilter === 'pending' ? 'active' : ''}`}
            onClick={() => setStatusFilter('pending')}
          >
            ⏳ Pending Approval
          </button>
          <button
            className={`filter-btn ${statusFilter === 'active' ? 'active' : ''}`}
            onClick={() => setStatusFilter('active')}
          >
            ✅ Active
          </button>
        </div>
      </div>

      {sessions.length === 0 ? (
        <div className="empty-state">
          <p>No {statusFilter === 'all' ? '' : statusFilter} registered sessions found.</p>
          <p>Visit <strong>Available Sessions</strong> to register for sessions!</p>
        </div>
      ) : (
        <div className="sessions-container">
          {(() => {
            // Group sessions by course
            const sessionsByCourse = {};
            const sessionsWithoutCourse = [];
            
            sessions.forEach(session => {
              if (session.course?.id) {
                const courseId = session.course.id;
                if (!sessionsByCourse[courseId]) {
                  sessionsByCourse[courseId] = {
                    course: session.course,
                    sessions: []
                  };
                }
                sessionsByCourse[courseId].sessions.push(session);
              } else {
                sessionsWithoutCourse.push(session);
              }
            });

            const courseGroups = Object.values(sessionsByCourse);
            
            return (
              <>
                {courseGroups.map((group, groupIdx) => (
                  <div key={`course-${group.course.id}`} className="course-group">
                    <div className="course-group-header">
                      <h2 className="course-group-title">
                        📚 {group.course.title}
                      </h2>
                      <span className="course-group-count">
                        {group.sessions.length} {group.sessions.length === 1 ? 'session' : 'sessions'}
                      </span>
                    </div>
                    <div className="course-sessions-list">
                      {group.sessions.map((session) => {
                        return renderSessionCard(session);
                      })}
                    </div>
                  </div>
                ))}
                {sessionsWithoutCourse.length > 0 && (
                  <div className="course-group">
                    <div className="course-group-header">
                      <h2 className="course-group-title">📋 Other Sessions</h2>
                      <span className="course-group-count">
                        {sessionsWithoutCourse.length} {sessionsWithoutCourse.length === 1 ? 'session' : 'sessions'}
                      </span>
                    </div>
                    <div className="course-sessions-list">
                      {sessionsWithoutCourse.map((session) => {
                        return renderSessionCard(session);
                      })}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {selectedHomework && (
        <HomeworkSubmissionModal
          homework={selectedHomework}
          onClose={() => setSelectedHomework(null)}
          onSubmit={handleSubmitHomework}
        />
      )}
    </div>
  );
};

const HomeworkSubmissionModal = ({ homework, onClose, onSubmit }) => {
  const [answer, setAnswer] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    // Filter for Word, PDF, ZIP files
    const allowedTypes = ['.doc', '.docx', '.pdf', '.zip'];
    const validFiles = selectedFiles.filter(file => {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      return allowedTypes.includes(ext);
    });
    setFiles(validFiles);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!answer.trim() && files.length === 0) {
      alert('Please provide an answer or attach a file.');
      return;
    }
    setUploading(true);
    try {
      await onSubmit(homework.id, answer, files);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div 
        className="modal-content" 
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '32px',
          maxWidth: '800px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          position: 'relative'
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#64748b',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f1f5f9';
            e.currentTarget.style.color = '#1e293b';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = '#64748b';
          }}
        >
          ✕
        </button>
        
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ 
            margin: '0 0 8px 0', 
            fontSize: '1.75rem', 
            fontWeight: '700', 
            color: '#1e293b',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '2rem' }}>📝</span>
            Submit Homework
          </h2>
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#f0f9ff',
            borderRadius: '10px',
            border: '2px solid #bae6fd',
            marginTop: '12px'
          }}>
            <h3 style={{ margin: '0 0 4px 0', fontSize: '1.1rem', color: '#1e293b' }}>
              {homework.title}
            </h3>
            {homework.description && (
              <p style={{ margin: '8px 0 0 0', color: '#475569', fontSize: '0.95rem', lineHeight: '1.5' }}>
                {homework.description}
              </p>
            )}
            {homework.topicName && (
              <p style={{ margin: '8px 0 0 0', color: '#64748b', fontSize: '0.9rem' }}>
                <strong>Topic:</strong> {homework.topicName}
              </p>
            )}
            <div style={{ marginTop: '12px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ 
                padding: '6px 12px', 
                backgroundColor: '#fef3c7', 
                color: '#92400e', 
                borderRadius: '8px', 
                fontSize: '0.85rem',
                fontWeight: '600'
              }}>
                📅 Due: {new Date(homework.dueDate).toLocaleString()}
              </span>
              {homework.rewardPoints > 0 && (
                <span style={{ 
                  padding: '6px 12px', 
                  backgroundColor: '#fef3c7', 
                  color: '#92400e', 
                  borderRadius: '8px', 
                  fontSize: '0.85rem',
                  fontWeight: '600'
                }}>
                  ⭐ {homework.rewardPoints} Points
                </span>
              )}
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '10px', 
              fontWeight: '600', 
              color: '#1e293b',
              fontSize: '1rem'
            }}>
              Your Answer <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <textarea
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              rows="10"
              placeholder="Enter your answer here. Be detailed and clear..."
              required={files.length === 0}
              style={{
                width: '100%',
                padding: '14px',
                border: '2px solid #e2e8f0',
                borderRadius: '10px',
                fontSize: '1rem',
                fontFamily: 'inherit',
                resize: 'vertical',
                transition: 'border-color 0.2s',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = '#667eea'}
              onBlur={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
            />
            <small style={{ 
              display: 'block', 
              marginTop: '8px', 
              color: '#64748b',
              fontSize: '0.85rem'
            }}>
              {files.length === 0 
                ? 'Required: Provide an answer or attach files'
                : 'Optional: You can add text answer in addition to files'}
            </small>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '10px', 
              fontWeight: '600', 
              color: '#1e293b',
              fontSize: '1rem'
            }}>
              Attach Files
            </label>
            <div style={{
              padding: '16px',
              border: '2px dashed #cbd5e1',
              borderRadius: '10px',
              backgroundColor: '#f8fafc',
              textAlign: 'center',
              transition: 'all 0.2s'
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.currentTarget.style.borderColor = '#667eea';
              e.currentTarget.style.backgroundColor = '#f0f9ff';
            }}
            onDragLeave={(e) => {
              e.currentTarget.style.borderColor = '#cbd5e1';
              e.currentTarget.style.backgroundColor = '#f8fafc';
            }}
            >
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                accept=".doc,.docx,.pdf,.zip"
                style={{
                  width: '100%',
                  padding: '8px',
                  cursor: 'pointer'
                }}
              />
              <p style={{ 
                margin: '8px 0 0 0', 
                color: '#64748b', 
                fontSize: '0.9rem' 
              }}>
                Supported formats: Word (.doc, .docx), PDF (.pdf), ZIP (.zip)
              </p>
            </div>
            {files.length > 0 && (
              <div style={{ 
                marginTop: '12px',
                padding: '12px',
                backgroundColor: '#f0fdf4',
                borderRadius: '10px',
                border: '2px solid #86efac'
              }}>
                <strong style={{ display: 'block', marginBottom: '8px', color: '#166534' }}>
                  Selected Files ({files.length}):
                </strong>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {files.map((file, idx) => (
                    <span 
                      key={idx} 
                      style={{
                        padding: '6px 12px',
                        backgroundColor: 'white',
                        border: '2px solid #86efac',
                        borderRadius: '8px',
                        fontSize: '0.9rem',
                        color: '#166534',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      📎 {file.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            justifyContent: 'flex-end',
            paddingTop: '20px',
            borderTop: '2px solid #e2e8f0'
          }}>
            <button 
              type="button" 
              onClick={onClose}
              style={{
                padding: '12px 24px',
                border: '2px solid #e2e8f0',
                borderRadius: '10px',
                background: 'white',
                color: '#475569',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '1rem',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#cbd5e1';
                e.currentTarget.style.backgroundColor = '#f8fafc';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e2e8f0';
                e.currentTarget.style.backgroundColor = 'white';
              }}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={uploading}
              style={{
                padding: '12px 24px',
                border: 'none',
                borderRadius: '10px',
                background: uploading ? '#94a3b8' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                cursor: uploading ? 'not-allowed' : 'pointer',
                fontWeight: '700',
                fontSize: '1rem',
                boxShadow: uploading ? 'none' : '0 4px 12px rgba(102, 126, 234, 0.4)',
                transition: 'all 0.2s',
                minWidth: '120px'
              }}
              onMouseEnter={(e) => {
                if (!uploading) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.5)';
                }
              }}
              onMouseLeave={(e) => {
                if (!uploading) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                }
              }}
            >
              {uploading ? '⏳ Submitting...' : '✅ Submit Homework'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StudentSessions;
